import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { getTf, DEFAULT_FAVORITES } from "../lib/symbols";
import { SymbolCatalog } from "../engine";
import { STRIP } from "../components/terminal/QuoteStrip";
import { useMarket } from "./MarketContext";
import { useTheme } from "./ThemeContext";

const Ctx = createContext(null);

export const DEFAULT_SETTINGS = {
  general: { theme: "dark", timezone: "UTC", currency: "USD", language: "en" },
  chart: { defaultSymbol: "1HZ50V", defaultInterval: "1s", chartType: "candlestick", futureFraction: 0.1, crosshair: true, magnet: true, grid: true, autoscale: true },
  trading: { mode: "demo", defaultStake: 1, defaultDuration: 5, confirm: true, maxDaily: 20, maxOpen: 3 },
  alerts: { browser: true, telegram: "", email: "" },
};
const merge = (base, over) => Object.fromEntries(Object.keys(base).map((k) => [k, { ...base[k], ...(over?.[k] || {}) }]));

// Workspace = the shared "normalized state" every page consumes: selected symbol/timeframe (deep-linked via
// /chart?symbol=&interval=), favourites, paper positions, per-user settings. Must live inside <BrowserRouter>.
export function WorkspaceProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { subscribeSymbols } = useMarket();
  const { setTheme } = useTheme();
  const [settings, setSettings] = useState(() => merge(DEFAULT_SETTINGS, JSON.parse(localStorage.getItem("rio.settings") || "null")));
  const [symbol, setSymbolState] = useState(() => params.get("symbol") || settings.chart.defaultSymbol);
  const [tf, setTfState] = useState(() => getTf(params.get("interval") || settings.chart.defaultInterval));
  const [favorites, setFavorites] = useState(DEFAULT_FAVORITES);
  const [catalogReady, setCatalogReady] = useState(false);
  const [openPositions, setOpenPositions] = useState([]);
  const [closedPositions, setClosedPositions] = useState([]);
  const onChart = location.pathname === "/chart";
  const savedSinceLoad = useRef(false);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // URL → state (refresh / back / forward / direct entry)
  useEffect(() => {
    if (!onChart) return;
    const s = params.get("symbol"), i = params.get("interval");
    if (s && s !== symbol) setSymbolState(s);
    if (i && i !== tf.id) setTfState(getTf(i));
  }, [params, onChart]); // eslint-disable-line

  const syncUrl = useCallback((s, t) => { if (location.pathname === "/chart") setParams({ symbol: s, interval: t }, { replace: false }); }, [location.pathname, setParams]);
  const setSymbol = useCallback((s) => { setSymbolState(s); syncUrl(s, tf.id); }, [syncUrl, tf.id]);
  const setTf = useCallback((t) => { setTfState(t); syncUrl(symbol, t.id); }, [syncUrl, symbol]);
  const openChart = useCallback((s = symbol, interval = tf.id) => { setSymbolState(s); setTfState(getTf(interval)); navigate(`/chart?symbol=${encodeURIComponent(s)}&interval=${interval}`); }, [navigate, symbol, tf.id]);
  const openTrade = useCallback((s = symbol) => { setSymbolState(s); navigate(`/demo-trade?symbol=${encodeURIComponent(s)}`); }, [navigate, symbol]);

  useEffect(() => { subscribeSymbols(Array.from(new Set([...favorites, ...STRIP, symbol]))); }, [favorites, symbol, subscribeSymbols]);

  const refreshPositions = useCallback(() => {
    api.get("/positions?status=open").then(({ data }) => setOpenPositions(data.positions)).catch(() => {});
    api.get("/positions?status=closed").then(({ data }) => setClosedPositions(data.positions)).catch(() => {});
  }, []);

  useEffect(() => {
    SymbolCatalog.load().then(() => setCatalogReady(true));
    api.get("/watchlist").then(({ data }) => { if (data.symbols?.length) setFavorites(data.symbols); }).catch(() => {});
    api.get("/settings").then(({ data }) => {
      if (savedSinceLoad.current) return; // a local save already happened — never overwrite it with the stale server copy
      const s = merge(DEFAULT_SETTINGS, data.data); setSettings(s); localStorage.setItem("rio.settings", JSON.stringify(s)); if (s.general.theme) setTheme(s.general.theme);
    }).catch(() => {});
    refreshPositions();
  }, []); // eslint-disable-line

  const saveSettings = useCallback(async (section, patch) => {
    savedSinceLoad.current = true;
    const cur = settingsRef.current;
    const next = { ...cur, [section]: { ...cur[section], ...patch } };
    setSettings(next);
    localStorage.setItem("rio.settings", JSON.stringify(next));
    if (section === "general" && patch.theme) setTheme(patch.theme);
    await api.put("/settings", { data: { [section]: next[section] } });
  }, [setTheme]);

  const toggleFav = useCallback(async (code) => {
    try { const { data } = await api.post("/watchlist/toggle", { symbol: code }); setFavorites(data.symbols); }
    catch { setFavorites((p) => (p.includes(code) ? p.filter((s) => s !== code) : [...p, code])); }
  }, []);
  const openPosition = useCallback(async (body) => { try { await api.post("/positions", body); refreshPositions(); } catch {} }, [refreshPositions]);
  const closePosition = useCallback(async (pos, exitPrice) => { try { await api.post(`/positions/${pos.id}/close`, { exit_price: exitPrice }); refreshPositions(); } catch {} }, [refreshPositions]);

  const value = useMemo(() => ({
    symbol, tf, setSymbol, setTf, openChart, openTrade, favorites, toggleFav, catalogReady,
    openPositions, closedPositions, refreshPositions, openPosition, closePosition, settings, saveSettings,
  }), [symbol, tf, setSymbol, setTf, openChart, openTrade, favorites, toggleFav, catalogReady, openPositions, closedPositions, refreshPositions, openPosition, closePosition, settings, saveSettings]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useWorkspace = () => useContext(Ctx);
